import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useRecordLegalAcknowledgementMutation,
  useTermsOfServiceQuery,
} from 'src/api/subscription/useSubscriptionApi';
import { ReactComponent as UserChevronLeftSVG } from 'src/assets/images/onboarding-trainer/components/sidebar/user-chevron-left.svg';
import { ReactComponent as SecurePaymentLockSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/secure-payment-lock.svg';
import './LegalScreen.scss';

export const TermsOfServiceScreen: FC = () => {
  const navigate = useNavigate();
  const { data: termsData } = useTermsOfServiceQuery();
  const { mutate: recordAck } = useRecordLegalAcknowledgementMutation();

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard/subscription');
    }
  };

  const handleContinue = () => {
    recordAck(termsData?.version ? { termsVersion: termsData.version } : undefined);
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard/subscription/checkout');
    }
  };

  return (
    <div className="legal-page-container">
      <div className="legal-page-header">
        <button type="button" className="legal-back-btn" onClick={handleBack}>
          <UserChevronLeftSVG />
          <span>{'Terms of Service'}</span>
        </button>
        <p className="legal-header-subtitle">
          {'Please review the terms that apply to your use of the platform and paid subscription'}
        </p>
      </div>

      <div className="legal-body-wrapper">
        <div className="legal-content-card">
          <div className="legal-section">
            <h3>{'1. Acceptance of Terms'}</h3>
            <p>
              {
                'By accessing or using miniPT (“the Platform”), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree, you may not use the Platform.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'2. Account Eligibility'}</h3>
            <p>
              {
                'You must be at least 18 years old and capable of forming a binding contract to create an account on miniPT. You agree to provide accurate and complete information during registration.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'3. Subscription & Billing'}</h3>
            <p>
              {
                'miniPT offers recurring subscription plans billed in advance. By subscribing, you authorise us to charge your selected payment method on a recurring basis until cancelled.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'4. Free Trial Terms'}</h3>
            <p>
              {
                'New eligible users may receive a 14-day free trial. At the end of the trial, your subscription will automatically begin and your payment method will be charged unless you cancel before the trial ends. One free trial per user.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'5. Cancellations & Refunds'}</h3>
            <p>
              {
                'You can cancel your subscription at any time from your account settings. Cancellations take effect at the end of the current billing period. We do not offer refunds for partial billing periods or unused subscription time, except as required by law.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'6. Trainer Responsibilities'}</h3>
            <p>
              {
                'You agree to use the Platform only for lawful purposes and in a professional manner. You are responsible for the accuracy of the information you provide and for obtaining any necessary consents from your clients.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'7. Content & Intellectual Property'}</h3>
            <p>
              {
                'All content, features, and materials on miniPT are owned by miniPT or its licensors and are protected by copyright, trademark, and other intellectual property laws. You retain ownership of the content you create, while granting miniPT a limited license to host and display it as needed to provide the service.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'8. Limitation of Liability'}</h3>
            <p>
              {
                'miniPT is provided “as is” and “as available.” To the fullest extent permitted by law, miniPT shall not be liable for any indirect, incidental, or consequential damages arising out of your use of the Platform.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'9. Contact Information'}</h3>
            <p>
              {
                'If you have any questions about these Terms, please contact us at legal@minipt.com or 12 Fitness Street, London, SW1A 1AA, United Kingdom.'
              }
            </p>
          </div>
        </div>

        <div className="legal-meta-card">
          <div className="meta-row">
            <span className="meta-label">{'Last updated'}</span>
            <span className="meta-value">{termsData?.effective_date || '—'}</span>
          </div>
          <div className="meta-row">
            <span className="meta-label">{'Version'}</span>
            <span className="meta-value">{termsData?.version || '—'}</span>
          </div>
        </div>

        <div className="legal-actions-footer">
          <button type="button" className="btn-read-continue" onClick={handleContinue}>
            {'Read & Continue'}
          </button>
          <div className="legal-bottom-disclaimer">
            <SecurePaymentLockSVG />
            <span>{'By continuing, you agree to be bound by these terms.'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
